import React, { useState } from 'react';
import CalcShell from '../CalcShell';
import { CNetRow, CNetActions, CNetResult, CNetHintBar, G } from './_cnet';

// Length units → meters
const LEN = { meters: 1, feet: 0.3048, inches: 0.0254, centimeters: 0.01, yards: 0.9144 };
const AREA_LABELS = ['meters', 'feet', 'yards', 'inches', 'centimeters'];

function toArea(sqm) {
  return {
    m2: sqm,
    ft2: sqm * 10.7639,
    yd2: sqm * 1.19599,
    in2: sqm * 1550.0031,
    cm2: sqm * 10000,
    acre: sqm / 4046.8564224,
  };
}

function ResultTable({ r, price, priceU }) {
  const totalArea = priceU === 'per square meters' ? r.m2 : priceU === 'per square feet' ? r.ft2 : priceU === 'per square yards' ? r.yd2 : r.m2;
  const cost = price ? Number(price) * totalArea : null;
  return (
    <CNetResult>
      <div className="text-slate-900 leading-8" data-testid="sqft-result">
        Area: <b className="text-[#4c7b25]">{G(r.ft2, 2)}</b> ft²<br/>
        <span className="ml-14">or <b className="text-[#4c7b25]">{G(r.m2, 2)}</b> m²</span><br/>
        <span className="ml-14">or <b className="text-[#4c7b25]">{G(r.yd2, 2)}</b> yd²</span><br/>
        <span className="ml-14">or <b className="text-[#4c7b25]">{G(r.acre, 6)}</b> acre</span>
      </div>
      {cost != null && !isNaN(cost) && (
        <div className="text-slate-900 mt-2">Estimated cost: <b className="text-[#4c7b25]">${G(cost, 2)}</b></div>
      )}
    </CNetResult>
  );
}

function UnitLen({ value, onChange, unit, onUnit }) {
  return (<>
    <input className="cnet-input" type="number" value={value} onChange={e => onChange(e.target.value)} />
    <select className="cnet-select" value={unit} onChange={e => onUnit(e.target.value)}>{AREA_LABELS.map(k => <option key={k}>{k}</option>)}</select>
  </>);
}

function PriceRow({ price, setPrice, priceU, setPriceU }) {
  return (
    <CNetRow label="Price (optional)">
      <div className="flex gap-1"><span className="pt-1">$</span><input className="cnet-input" type="number" value={price} onChange={e => setPrice(e.target.value)} /></div>
      <select className="cnet-select" value={priceU} onChange={e => setPriceU(e.target.value)}>
        {['per square meters', 'per square feet', 'per square yards'].map(k => <option key={k}>{k}</option>)}
      </select>
    </CNetRow>
  );
}

/* ---------- Shape sections ---------- */

function Rectangle() {
  const [l, sL] = useState(30), [lU, sLU] = useState('meters');
  const [w, sW] = useState(20), [wU, sWU] = useState('meters');
  const [q, sQ] = useState(1); const [p, sP] = useState(''); const [pU, sPU] = useState('per square meters');
  const [r, sR] = useState(null);
  const calc = () => sR(toArea(Number(l) * LEN[lU] * Number(w) * LEN[wU] * Number(q)));
  return (
    <>
      <h3 className="cnet-section-title">Rectangle</h3>
      <div className="grid lg:grid-cols-[1fr_180px] gap-4 items-start">
        <div className="cnet-panel">
          <CNetRow label="Length"><UnitLen value={l} onChange={sL} unit={lU} onUnit={sLU} /></CNetRow>
          <CNetRow label="Width"><UnitLen value={w} onChange={sW} unit={wU} onUnit={sWU} /></CNetRow>
          <div className="cnet-row-2col"><div className="cnet-label">Quantity</div><input className="cnet-input" type="number" value={q} onChange={e => sQ(e.target.value)} /></div>
          <PriceRow price={p} setPrice={sP} priceU={pU} setPriceU={sPU} />
          <CNetActions onCalc={calc} onClear={() => sR(null)} testId="sqft-rect" />
        </div>
        <svg viewBox="0 0 140 100" className="w-36 mx-auto"><rect x="20" y="25" width="100" height="50" fill="none" stroke="#0f172a"/><text x="70" y="20" textAnchor="middle" fontSize="9" fill="#2563eb">l</text><text x="130" y="55" fontSize="9" fill="#2563eb">w</text></svg>
      </div>
      {r && <ResultTable r={r} price={p} priceU={pU} />}
    </>
  );
}

function RectangleBorder() {
  const [l, sL] = useState(30), [lU, sLU] = useState('meters');
  const [w, sW] = useState(20), [wU, sWU] = useState('meters');
  const [bw, sBw] = useState(2), [bwU, sBwU] = useState('meters');
  const [q, sQ] = useState(1); const [p, sP] = useState(''); const [pU, sPU] = useState('per square meters');
  const [r, sR] = useState(null);
  const calc = () => {
    const L = Number(l) * LEN[lU], W = Number(w) * LEN[wU], B = Number(bw) * LEN[bwU];
    sR(toArea((L * W - Math.max(0, L - 2 * B) * Math.max(0, W - 2 * B)) * Number(q)));
  };
  return (
    <>
      <h3 className="cnet-section-title">Rectangle Border</h3>
      <div className="grid lg:grid-cols-[1fr_180px] gap-4 items-start">
        <div className="cnet-panel">
          <CNetRow label="Length"><UnitLen value={l} onChange={sL} unit={lU} onUnit={sLU} /></CNetRow>
          <CNetRow label="Width"><UnitLen value={w} onChange={sW} unit={wU} onUnit={sWU} /></CNetRow>
          <CNetRow label="Border Width"><UnitLen value={bw} onChange={sBw} unit={bwU} onUnit={sBwU} /></CNetRow>
          <div className="cnet-row-2col"><div className="cnet-label">Quantity</div><input className="cnet-input" type="number" value={q} onChange={e => sQ(e.target.value)} /></div>
          <PriceRow price={p} setPrice={sP} priceU={pU} setPriceU={sPU} />
          <CNetActions onCalc={calc} onClear={() => sR(null)} testId="sqft-rectb" />
        </div>
        <svg viewBox="0 0 140 100" className="w-36 mx-auto">
          <rect x="10" y="15" width="120" height="70" fill="#e5e7eb" stroke="#0f172a"/>
          <rect x="30" y="30" width="80" height="40" fill="#fff" stroke="#0f172a"/>
          <text x="70" y="12" textAnchor="middle" fontSize="9" fill="#2563eb">outer l</text>
          <text x="70" y="95" textAnchor="middle" fontSize="9" fill="#2563eb">border w</text>
        </svg>
      </div>
      {r && <ResultTable r={r} price={p} priceU={pU} />}
    </>
  );
}

function Circle() {
  const [d, sD] = useState(30), [dU, sDU] = useState('meters');
  const [q, sQ] = useState(1); const [p, sP] = useState(''); const [pU, sPU] = useState('per square meters');
  const [r, sR] = useState(null);
  const calc = () => { const D = Number(d) * LEN[dU]; sR(toArea(Math.PI * (D / 2) ** 2 * Number(q))); };
  return (
    <>
      <h3 className="cnet-section-title">Circle</h3>
      <div className="grid lg:grid-cols-[1fr_180px] gap-4 items-start">
        <div className="cnet-panel">
          <CNetRow label="Diameter"><UnitLen value={d} onChange={sD} unit={dU} onUnit={sDU} /></CNetRow>
          <div className="cnet-row-2col"><div className="cnet-label">Quantity</div><input className="cnet-input" type="number" value={q} onChange={e => sQ(e.target.value)} /></div>
          <PriceRow price={p} setPrice={sP} priceU={pU} setPriceU={sPU} />
          <CNetActions onCalc={calc} onClear={() => sR(null)} testId="sqft-circle" />
        </div>
        <svg viewBox="0 0 120 100" className="w-32 mx-auto"><circle cx="60" cy="50" r="30" fill="none" stroke="#0f172a"/><line x1="60" y1="50" x2="90" y2="50" stroke="#2563eb"/><text x="72" y="46" fontSize="9" fill="#2563eb">r</text></svg>
      </div>
      {r && <ResultTable r={r} price={p} priceU={pU} />}
    </>
  );
}

function Ring() {
  const [d, sD] = useState(30), [dU, sDU] = useState('meters');
  const [bw, sBw] = useState(2), [bwU, sBwU] = useState('meters');
  const [q, sQ] = useState(1); const [p, sP] = useState(''); const [pU, sPU] = useState('per square meters');
  const [r, sR] = useState(null);
  const calc = () => {
    const R = (Number(d) * LEN[dU]) / 2, B = Number(bw) * LEN[bwU];
    sR(toArea((Math.PI * (R * R - Math.max(0, R - B) ** 2)) * Number(q)));
  };
  return (
    <>
      <h3 className="cnet-section-title">Ring</h3>
      <div className="grid lg:grid-cols-[1fr_180px] gap-4 items-start">
        <div className="cnet-panel">
          <CNetRow label="Outer Diameter"><UnitLen value={d} onChange={sD} unit={dU} onUnit={sDU} /></CNetRow>
          <CNetRow label="Border Width"><UnitLen value={bw} onChange={sBw} unit={bwU} onUnit={sBwU} /></CNetRow>
          <div className="cnet-row-2col"><div className="cnet-label">Quantity</div><input className="cnet-input" type="number" value={q} onChange={e => sQ(e.target.value)} /></div>
          <PriceRow price={p} setPrice={sP} priceU={pU} setPriceU={sPU} />
          <CNetActions onCalc={calc} onClear={() => sR(null)} testId="sqft-ring" />
        </div>
        <svg viewBox="0 0 120 100" className="w-32 mx-auto"><circle cx="60" cy="50" r="35" fill="#e5e7eb" stroke="#0f172a"/><circle cx="60" cy="50" r="20" fill="#fff" stroke="#0f172a"/><line x1="60" y1="50" x2="95" y2="50" stroke="#2563eb"/><text x="76" y="46" fontSize="8" fill="#2563eb">R</text><line x1="60" y1="50" x2="60" y2="30" stroke="#dc2626"/><text x="62" y="42" fontSize="8" fill="#dc2626">r</text></svg>
      </div>
      {r && <ResultTable r={r} price={p} priceU={pU} />}
    </>
  );
}

function TriangleEdges() {
  const [a, sA] = useState(30), [aU, sAU] = useState('meters');
  const [b, sB] = useState(45), [bU, sBU] = useState('meters');
  const [c, sC] = useState(50), [cU, sCU] = useState('meters');
  const [q, sQ] = useState(1); const [p, sP] = useState(''); const [pU, sPU] = useState('per square meters');
  const [r, sR] = useState(null);
  const calc = () => {
    const A = Number(a) * LEN[aU], B = Number(b) * LEN[bU], C = Number(c) * LEN[cU];
    const s = (A + B + C) / 2;
    sR(toArea(Math.sqrt(Math.max(0, s * (s - A) * (s - B) * (s - C))) * Number(q)));
  };
  return (
    <>
      <h3 className="cnet-section-title">Triangle with Edge Lengths</h3>
      <div className="grid lg:grid-cols-[1fr_180px] gap-4 items-start">
        <div className="cnet-panel">
          <CNetRow label="Edge 1 (a)"><UnitLen value={a} onChange={sA} unit={aU} onUnit={sAU} /></CNetRow>
          <CNetRow label="Edge 2 (b)"><UnitLen value={b} onChange={sB} unit={bU} onUnit={sBU} /></CNetRow>
          <CNetRow label="Edge 3 (c)"><UnitLen value={c} onChange={sC} unit={cU} onUnit={sCU} /></CNetRow>
          <div className="cnet-row-2col"><div className="cnet-label">Quantity</div><input className="cnet-input" type="number" value={q} onChange={e => sQ(e.target.value)} /></div>
          <PriceRow price={p} setPrice={sP} priceU={pU} setPriceU={sPU} />
          <CNetActions onCalc={calc} onClear={() => sR(null)} testId="sqft-triedges" />
        </div>
        <svg viewBox="0 0 120 100" className="w-32 mx-auto"><polygon points="20,80 100,80 40,20" fill="none" stroke="#0f172a"/><text x="55" y="94" fontSize="8" fill="#2563eb">side a</text><text x="20" y="52" fontSize="8" fill="#2563eb">side b</text><text x="72" y="52" fontSize="8" fill="#2563eb">side c</text></svg>
      </div>
      {r && <ResultTable r={r} price={p} priceU={pU} />}
    </>
  );
}

function TriangleBH() {
  const [b, sB] = useState(30), [bU, sBU] = useState('meters');
  const [h, sH] = useState(20), [hU, sHU] = useState('meters');
  const [q, sQ] = useState(1); const [p, sP] = useState(''); const [pU, sPU] = useState('per square meters');
  const [r, sR] = useState(null);
  const calc = () => { const B = Number(b) * LEN[bU], H = Number(h) * LEN[hU]; sR(toArea(0.5 * B * H * Number(q))); };
  return (
    <>
      <h3 className="cnet-section-title">Triangle with Base &amp; Height</h3>
      <div className="grid lg:grid-cols-[1fr_180px] gap-4 items-start">
        <div className="cnet-panel">
          <CNetRow label="Base"><UnitLen value={b} onChange={sB} unit={bU} onUnit={sBU} /></CNetRow>
          <CNetRow label="Height"><UnitLen value={h} onChange={sH} unit={hU} onUnit={sHU} /></CNetRow>
          <div className="cnet-row-2col"><div className="cnet-label">Quantity</div><input className="cnet-input" type="number" value={q} onChange={e => sQ(e.target.value)} /></div>
          <PriceRow price={p} setPrice={sP} priceU={pU} setPriceU={sPU} />
          <CNetActions onCalc={calc} onClear={() => sR(null)} testId="sqft-tribh" />
        </div>
        <svg viewBox="0 0 120 100" className="w-32 mx-auto"><polygon points="20,80 100,80 60,20" fill="none" stroke="#0f172a"/><text x="55" y="50" fontSize="8" fill="#2563eb">Height</text><text x="55" y="94" fontSize="8" fill="#2563eb">Base</text></svg>
      </div>
      {r && <ResultTable r={r} price={p} priceU={pU} />}
    </>
  );
}

function Trapezoid() {
  const [b1, s1] = useState(30), [b1U, s1U] = useState('meters');
  const [b2, s2] = useState(45), [b2U, s2U] = useState('meters');
  const [h, sH] = useState(20), [hU, sHU] = useState('meters');
  const [q, sQ] = useState(1); const [p, sP] = useState(''); const [pU, sPU] = useState('per square meters');
  const [r, sR] = useState(null);
  const calc = () => {
    const B1 = Number(b1) * LEN[b1U], B2 = Number(b2) * LEN[b2U], H = Number(h) * LEN[hU];
    sR(toArea(0.5 * (B1 + B2) * H * Number(q)));
  };
  return (
    <>
      <h3 className="cnet-section-title">Trapezoid</h3>
      <div className="grid lg:grid-cols-[1fr_180px] gap-4 items-start">
        <div className="cnet-panel">
          <CNetRow label="Base 1"><UnitLen value={b1} onChange={s1} unit={b1U} onUnit={s1U} /></CNetRow>
          <CNetRow label="Base 2"><UnitLen value={b2} onChange={s2} unit={b2U} onUnit={s2U} /></CNetRow>
          <CNetRow label="Height"><UnitLen value={h} onChange={sH} unit={hU} onUnit={sHU} /></CNetRow>
          <div className="cnet-row-2col"><div className="cnet-label">Quantity</div><input className="cnet-input" type="number" value={q} onChange={e => sQ(e.target.value)} /></div>
          <PriceRow price={p} setPrice={sP} priceU={pU} setPriceU={sPU} />
          <CNetActions onCalc={calc} onClear={() => sR(null)} testId="sqft-trap" />
        </div>
        <svg viewBox="0 0 120 100" className="w-32 mx-auto"><polygon points="30,80 90,80 100,30 20,30" fill="none" stroke="#0f172a"/><text x="60" y="94" fontSize="8" fill="#2563eb">base b</text><text x="60" y="26" fontSize="8" fill="#2563eb">top a</text><text x="105" y="55" fontSize="8" fill="#2563eb">h</text></svg>
      </div>
      {r && <ResultTable r={r} price={p} priceU={pU} />}
    </>
  );
}

function Sector() {
  const [rad, sRad] = useState(30), [rU, sRU] = useState('meters');
  const [ang, sAng] = useState(90);
  const [q, sQ] = useState(1); const [p, sP] = useState(''); const [pU, sPU] = useState('per square meters');
  const [r, sR] = useState(null);
  const calc = () => { const R = Number(rad) * LEN[rU]; sR(toArea(0.5 * R * R * (Number(ang) * Math.PI / 180) * Number(q))); };
  return (
    <>
      <h3 className="cnet-section-title">Sector</h3>
      <div className="grid lg:grid-cols-[1fr_180px] gap-4 items-start">
        <div className="cnet-panel">
          <CNetRow label="Radius"><UnitLen value={rad} onChange={sRad} unit={rU} onUnit={sRU} /></CNetRow>
          <CNetRow label="Angle"><input className="cnet-input" type="number" value={ang} onChange={e => sAng(e.target.value)} /><span className="text-sm text-slate-500 pt-2">degree °</span></CNetRow>
          <div className="cnet-row-2col"><div className="cnet-label">Quantity</div><input className="cnet-input" type="number" value={q} onChange={e => sQ(e.target.value)} /></div>
          <PriceRow price={p} setPrice={sP} priceU={pU} setPriceU={sPU} />
          <CNetActions onCalc={calc} onClear={() => sR(null)} testId="sqft-sector" />
        </div>
        <svg viewBox="0 0 120 100" className="w-32 mx-auto"><path d="M60,50 L100,50 A40,40 0 0,0 60,10 Z" fill="none" stroke="#0f172a"/><text x="80" y="60" fontSize="8" fill="#2563eb">r</text><text x="55" y="30" fontSize="8" fill="#2563eb">θ</text></svg>
      </div>
      {r && <ResultTable r={r} price={p} priceU={pU} />}
    </>
  );
}

function Parallelogram() {
  const [b, sB] = useState(30), [bU, sBU] = useState('meters');
  const [h, sH] = useState(20), [hU, sHU] = useState('meters');
  const [q, sQ] = useState(1); const [p, sP] = useState(''); const [pU, sPU] = useState('per square meters');
  const [r, sR] = useState(null);
  const calc = () => { const B = Number(b) * LEN[bU], H = Number(h) * LEN[hU]; sR(toArea(B * H * Number(q))); };
  return (
    <>
      <h3 className="cnet-section-title">Parallelogram</h3>
      <div className="grid lg:grid-cols-[1fr_180px] gap-4 items-start">
        <div className="cnet-panel">
          <CNetRow label="Base"><UnitLen value={b} onChange={sB} unit={bU} onUnit={sBU} /></CNetRow>
          <CNetRow label="Height"><UnitLen value={h} onChange={sH} unit={hU} onUnit={sHU} /></CNetRow>
          <div className="cnet-row-2col"><div className="cnet-label">Quantity</div><input className="cnet-input" type="number" value={q} onChange={e => sQ(e.target.value)} /></div>
          <PriceRow price={p} setPrice={sP} priceU={pU} setPriceU={sPU} />
          <CNetActions onCalc={calc} onClear={() => sR(null)} testId="sqft-para" />
        </div>
        <svg viewBox="0 0 120 100" className="w-32 mx-auto"><polygon points="20,70 90,70 100,30 30,30" fill="none" stroke="#0f172a"/><text x="55" y="84" fontSize="8" fill="#2563eb">base b</text><text x="105" y="55" fontSize="8" fill="#2563eb">h</text></svg>
      </div>
      {r && <ResultTable r={r} price={p} priceU={pU} />}
    </>
  );
}

export default function SquareFootageCalc() {
  return (
    <CalcShell look="cnet" showActions={false} title="Square Footage Calculator" subtitle="Estimate the square footage of a lot, house, or other surfaces in several common shapes.">
      <p className="text-sm text-slate-700 mb-3">The Square Footage Calculator estimates the square footage of a lot, house, or other surfaces in several common shapes. If the surface is complex in shape, it may be possible to section the surface into simple shapes and add their square footages together.</p>
      <CNetHintBar />
      <Rectangle />
      <RectangleBorder />
      <Circle />
      <Ring />
      <TriangleEdges />
      <TriangleBH />
      <Trapezoid />
      <Sector />
      <Parallelogram />
      <div className="mt-6">
        <h3 className="cnet-section-title">Reference:</h3>
        <div className="text-sm text-slate-800 leading-7">
          1 acre = 43,560 square foot<br/>
          1 square yard = 9 square foot<br/>
          1 square meter = 10.76 square foot<br/>
          1 square inch = 0.00064516 square foot
        </div>
      </div>
    </CalcShell>
  );
}
